/**
 * /places/:placeId/edit — pre-populated form for editing place details.
 * Reuses the same field surface as AddPlaceForm but routed inline (per AC-038).
 *
 * Architecture: a thin param-validation wrapper (PlaceEditRoute) reads the
 * router param and validates it. The inner form component (PlaceEditForm)
 * hosts ALL hooks so the rules-of-hooks invariant holds across param
 * transitions (e.g. programmatic navigation between valid/invalid IDs on the
 * same component instance).
 */
import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import type {
  CategoryId,
  PlaceId,
  PlaceStatus,
  TagId,
} from '@shared/types';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { TextInput } from '../../components/ui/TextInput';
import { FormField } from '../../components/ui/FormField';
import { EmptyState } from '../../components/ui/EmptyState';
import { useActiveHouseholdId } from '../../lib/household';
import {
  useCategories,
  usePlace,
  useTags,
  useUpdatePlace,
} from '../../lib/queries';
import { useToast } from '../../lib/store';
import { useErrorToasts } from '../../lib/errorToast';
import { cn } from '../../lib/cn';

const STATUSES: PlaceStatus[] = ['untried', 'go-to', 'neutral', 'avoid', 'archived'];

export default function PlaceEditRoute() {
  const { placeId } = useParams<{ placeId: string }>();
  if (!placeId || !placeId.startsWith('plc_')) {
    return <Navigate to="/" replace />;
  }
  return <PlaceEditForm placeId={placeId as PlaceId} />;
}

function PlaceEditForm({ placeId: id }: { placeId: PlaceId }) {
  const navigate = useNavigate();
  const householdId = useActiveHouseholdId();
  const toast = useToast();
  const errors = useErrorToasts();

  const place = usePlace(householdId, id);
  const categories = useCategories(householdId);
  const tagsQ = useTags(householdId);
  const update = useUpdatePlace(householdId, id);

  const [name, setName] = React.useState('');
  const [categoryId, setCategoryId] = React.useState<CategoryId | ''>('');
  const [address, setAddress] = React.useState('');
  const [status, setStatus] = React.useState<PlaceStatus>('untried');
  const [tagIds, setTagIds] = React.useState<TagId[]>([]);
  const [hydrated, setHydrated] = React.useState(false);
  const [touched, setTouched] = React.useState(false);

  // Pre-populate from the loaded detail. Run once when data arrives.
  React.useEffect(() => {
    if (!place.data || hydrated) return;
    setName(place.data.name);
    setCategoryId(place.data.category_id);
    setAddress(place.data.address ?? '');
    setStatus(place.data.status);
    setTagIds(place.data.tag_ids);
    setHydrated(true);
  }, [place.data, hydrated]);

  if (place.isPending) {
    return (
      <div data-testid="place-edit-skeleton">
        <Skeleton className="h-3 w-24 mb-3" />
        <Skeleton className="h-12 w-2/3 mb-12" />
        <div className="space-y-6 max-w-lg">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  if (place.error || !place.data) {
    return (
      <EmptyState
        title="We couldn't load that place."
        description="It may have been removed, or you may have lost connection."
        cta={
          <Button variant="primary" onClick={() => navigate('/places')}>
            Back to places
          </Button>
        }
      />
    );
  }

  const cats = categories.data?.categories ?? [];
  const tags = tagsQ.data?.tags ?? [];
  const nameError = touched && !name.trim() ? 'Give the place a name.' : '';
  const categoryError = touched && !categoryId ? 'Choose a category.' : '';
  const formInvalid = Boolean(nameError) || Boolean(categoryError);
  // Address was set on hydration; we track its prior value so a user-cleared
  // address can be sent as `null` (allowed by the backend's nullable schema).
  const hadAddress = Boolean(place.data.address);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    if (!name.trim() || !categoryId) return;
    try {
      const trimmedAddress = address.trim();
      await update.mutateAsync({
        name: name.trim(),
        category_id: categoryId,
        // Distinguish "user cleared" (had address, now blank → send null)
        // from "user never touched" (no address, still blank → omit).
        ...(trimmedAddress
          ? { address: trimmedAddress }
          : hadAddress
            ? { address: null }
            : {}),
        status,
        ...(tagIds.length ? { tag_ids: tagIds } : {}),
      });
      toast.success('Place updated.');
      navigate(`/places/${id}`);
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error
            ? err.message
            : "We couldn't update the place.",
        );
      }
    }
  }

  return (
    <article>
      <PageHeader eyebrow="Edit place" title={place.data.name} />

      <form onSubmit={onSubmit} noValidate className="space-y-6 max-w-lg">
        {formInvalid && touched ? (
          <div
            className="px-4 py-3 border border-error-muted/40 bg-error-soft"
            role="alert"
          >
            <p className="font-serif italic text-sm text-error-muted leading-snug">
              A few details need attention before we can save these changes.
            </p>
          </div>
        ) : null}

        <FormField
          label="Name"
          htmlFor="edit-place-name"
          {...(nameError ? { error: nameError } : {})}
        >
          <TextInput
            id="edit-place-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </FormField>

        <FormField
          label="Category"
          htmlFor="edit-place-category"
          {...(categoryError ? { error: categoryError } : {})}
        >
          <select
            id="edit-place-category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value as CategoryId)}
            className={cn(
              'block w-full bg-transparent border-0 border-b border-ink-300 px-0 py-3',
              'font-sans text-base text-ink-900',
              'focus:border-b-2 focus:border-accent focus:outline-none',
            )}
            required
          >
            <option value="">Pick a category</option>
            {cats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Address" optional htmlFor="edit-place-address">
          <TextInput
            id="edit-place-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="10 Pine Ave"
          />
        </FormField>

        <FormField label="Status" htmlFor="edit-place-status">
          <select
            id="edit-place-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as PlaceStatus)}
            className={cn(
              'block w-full bg-transparent border-0 border-b border-ink-300 px-0 py-3',
              'font-sans text-base text-ink-900',
              'focus:border-b-2 focus:border-accent focus:outline-none',
            )}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormField>

        {tags.length ? (
          <FormField label="Tags" optional>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => {
                const active = tagIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() =>
                      setTagIds((prev) =>
                        active
                          ? prev.filter((tid) => tid !== t.id)
                          : [...prev, t.id],
                      )
                    }
                    aria-pressed={active}
                    className={cn(
                      'px-3 py-1 border text-xs lowercase rounded-full',
                      'transition-colors duration-200 ease-out-slow',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
                      active
                        ? 'bg-accent-soft border-accent text-accent'
                        : 'bg-transparent border-ink-200 text-ink-700 hover:bg-surface-2',
                    )}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </FormField>
        ) : null}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(`/places/${id}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            loadingLabel="Saving…"
            disabled={update.isPending}
          >
            Save changes
          </Button>
        </div>
      </form>
    </article>
  );
}
